import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { indentFor, orden } from "../lib/folderTree.js";
import type { MailFolder } from "../lib/types.js";
import { IconFolderPlus, IconX, iconForSpecialUse } from "./icons.js";

/**
 * "Verplaatsen naar…" — de mappenkiezer.
 *
 * Dit is niet alleen het mobiele alternatief voor slepen: het is ook de
 * toetsenbord- en screenreaderroute op desktop. Slepen mag nooit de enige
 * manier zijn om post op te bergen.
 *
 * Eén component, twee gedaanten via het bestaande breekpunt van 900px: een
 * gecentreerde kaart op desktop, een lade vanaf de onderrand op de telefoon.
 * Dat verschil zit volledig in styles.css, niet in een JS-tak.
 */

interface MoveToSheetProps {
  title: string;
  folders: MailFolder[];
  /** Bronmap, of bij een mapverplaatsing de map zelf plus zijn nakomelingen. */
  disabledPaths: ReadonlySet<string>;
  /** Toont "Hoogste niveau" als doel; alleen zinvol bij het verplaatsen van een map. */
  allowRoot?: boolean;
  /** Aanwezig = er verschijnt een knop "Nieuwe map…" onderaan. */
  onCreateFolder?: () => void;
  onPick: (target: MailFolder | null) => void;
  onClose: () => void;
}

/** Vanaf dit aantal mappen is scrollen alleen niet meer werkbaar. */
const FILTER_VANAF = 12;

export function MoveToSheet({
  title,
  folders,
  disabledPaths,
  allowRoot = false,
  onCreateFolder,
  onPick,
  onClose,
}: MoveToSheetProps): ReactNode {
  const [filter, setFilter] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLElement>(null);

  // De focus hoort terug naar waar hij vandaan kwam; anders begint een
  // toetsenbordgebruiker na het sluiten weer bovenaan de pagina.
  useEffect(() => {
    const opener = document.activeElement;
    firstFieldRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      // Op mobiel bedekt deze lade het hele scherm. Zonder deze val tabt een
      // toetsenbordgebruiker de berichtenlijst in die eronder ligt.
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = useMemo(() => {
    const { special, rest } = orden(folders);
    const alle = [...special, ...rest];
    const term = filter.trim().toLowerCase();
    if (term === "") return alle;
    // Bij filteren vervalt de inspringing: de ouder staat er dan vaak niet meer
    // boven, en dan wijst inspringen nergens meer naar.
    return alle
      .filter((row) => row.folder.name.toLowerCase().includes(term))
      .map((row) => ({ ...row, depth: 0 }));
  }, [folders, filter]);

  const toonFilter = folders.length >= FILTER_VANAF;

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      {/* Klikken naast de lade sluit hem, net als bij het opstelvenster. */}
      <button type="button" className="sheet-dismiss" aria-label="Sluiten" onClick={onClose} />

      <div className="sheet" ref={panelRef}>
        <div className="sheet-head">
          <h2 className="sheet-title">{title}</h2>
          <button
            type="button"
            className="icon-button"
            aria-label="Sluiten"
            onClick={onClose}
            ref={toonFilter ? undefined : (firstFieldRef as React.Ref<HTMLButtonElement>)}
          >
            <IconX />
          </button>
        </div>

        {toonFilter && (
          <div className="sheet-filter">
            <input
              ref={firstFieldRef as React.Ref<HTMLInputElement>}
              type="search"
              className="field-input"
              value={filter}
              placeholder="Zoek een map"
              aria-label="Mappen filteren"
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}

        <ul className="sheet-list">
          {allowRoot && (
            <li>
              <button type="button" className="folder-row" onClick={() => onPick(null)}>
                <span className="folder-icon">{iconForSpecialUse(null)}</span>
                <span className="folder-name">Bovenaan (geen bovenliggende map)</span>
              </button>
            </li>
          )}

          {rows.map(({ folder, depth }) => {
            const disabled = disabledPaths.has(folder.path) || !folder.selectable;
            return (
              <li key={folder.path}>
                <button
                  type="button"
                  className="folder-row"
                  style={{ paddingLeft: indentFor(depth) }}
                  disabled={disabled}
                  aria-disabled={disabled || undefined}
                  onClick={() => onPick(folder)}
                >
                  <span className="folder-icon">{iconForSpecialUse(folder.specialUse)}</span>
                  <span className="folder-name">{folder.name}</span>
                </button>
              </li>
            );
          })}

          {rows.length === 0 && <li className="pane-message">Geen map gevonden.</li>}
        </ul>

        {onCreateFolder !== undefined && (
          <div className="sheet-foot">
            <button type="button" className="button button--ghost" onClick={onCreateFolder}>
              <IconFolderPlus />
              Nieuwe map…
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
